import { useState, useMemo } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  Package, 
  Ruler, 
  Palette, 
  Settings, 
  ShoppingCart,
  Info,
  Calculator
} from 'lucide-react';
import {
  CABINET_IMAGES,
  CABINET_TYPES,
  BOX_MATERIALS,
  DOOR_MATERIALS,
  DOOR_STYLES,
  COLORS,
  UPGRADES,
  WALL_CABINET_WIDTHS,
  WALL_CABINET_HEIGHTS,
  BASE_CABINET_WIDTHS,
  BASE_CABINET_HEIGHT,
  MAX_CUSTOM_WIDTH,
  MAX_CUSTOM_HEIGHT,
  MIN_CUSTOM_WIDTH,
  MIN_CUSTOM_HEIGHT,
  MIN_CUSTOM_DEPTH,
  MAX_CUSTOM_DEPTH,
  STANDARD_DEPTH,
  findById,
  getLabel,
  getColorImage,
  calculatePrice
} from '@/lib/cabinetConfig';

export default function CabinetsPage() {
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({
    cabinetType: 'wall',
    boxMaterial: 'white-melamine',
    doorMaterial: 'unfinished-mdf',
    doorStyle: 'shaker',
    color: 'white',
    width: 6,
    height: 24,
    customWidth: null,
    customHeight: null,
    customDepth: null,
    useCustomSize: false,
    quantity: 1,
    upgrades: [],
  });

  const { addToCart } = useCart();
  const pricing = useMemo(() => calculatePrice(config), [config]);
  
  // Determine available dimensions based on cabinet type
  const isWallType = config.cabinetType === 'wall';
  const availableWidths = isWallType ? WALL_CABINET_WIDTHS : BASE_CABINET_WIDTHS;
  const availableHeights = isWallType ? WALL_CABINET_HEIGHTS : [BASE_CABINET_HEIGHT];
  
  // Check if custom depth option should be shown (height > 42")
  const actualHeight = config.useCustomSize && config.customHeight ? config.customHeight : config.height;
  const showDepthOption = actualHeight > 42;

  const updateConfig = (updates) => {
    setConfig(prev => {
      const newConfig = { ...prev, ...updates };
      
      // Reset dimensions when cabinet type changes
      if (updates.cabinetType && updates.cabinetType !== prev.cabinetType) {
        const isWall = updates.cabinetType === 'wall';
        newConfig.width = isWall ? WALL_CABINET_WIDTHS[0] : BASE_CABINET_WIDTHS[0];
        newConfig.height = isWall ? WALL_CABINET_HEIGHTS[0] : BASE_CABINET_HEIGHT;
        newConfig.useCustomSize = false;
        newConfig.customWidth = null;
        newConfig.customHeight = null;
        newConfig.customDepth = null;
      }
      
      // Reset custom depth if height drops below 42"
      if (updates.height !== undefined || updates.customHeight !== undefined) {
        const newHeight = newConfig.useCustomSize && newConfig.customHeight ? newConfig.customHeight : newConfig.height;
        if (newHeight <= 42) {
          newConfig.customDepth = null;
        }
      }
      
      return newConfig;
    });
  };

  const toggleUpgrade = (id) => {
    const curr = config.upgrades || [];
    updateConfig({ upgrades: curr.includes(id) ? curr.filter(x => x !== id) : [...curr, id] });
  };

  const handleAddToCart = () => {
    const cabinetTypeData = findById(CABINET_TYPES, config.cabinetType);
    const colorData = findById(COLORS, config.color);
    const boxMaterialData = findById(BOX_MATERIALS, config.boxMaterial);
    const doorMaterialData = findById(DOOR_MATERIALS, config.doorMaterial);
    
    const actualWidth = config.useCustomSize && config.customWidth ? config.customWidth : config.width;
    const actualHeight = config.useCustomSize && config.customHeight ? config.customHeight : config.height;
    
    const customProduct = {
      id: `custom-${Date.now()}`,
      name: `Custom ${cabinetTypeData?.name || 'Cabinet'}`,
      description: `${actualWidth}" W × ${actualHeight}" H, ${boxMaterialData?.name || ''}, ${doorMaterialData?.name || ''}, ${colorData?.name || ''}`,
      price: pricing.unitPrice,
      images: [getColorImage(config.color)],
      configuration: config,
    };

    for (let i = 0; i < config.quantity; i++) {
      addToCart({ product_id: customProduct.id, quantity: 1, product: customProduct });
    }
    toast.success(`Added ${config.quantity} custom cabinet(s) to cart!`);
  };

  // Build step content based on current step
  let stepContent = null;

  if (step === 1) {
    // Cabinet Type Selection
    const typeCards = [];
    for (let i = 0; i < CABINET_TYPES.length; i++) {
      const t = CABINET_TYPES[i];
      const sel = config.cabinetType === t.id;
      const basePrice = Math.round(50 * t.multiplier);
      typeCards.push(
        <div
          key={t.id}
          className={`flex flex-col p-6 bg-white border-2 rounded-lg cursor-pointer transition-all ${sel ? 'border-[#8B5E3C] bg-[#F9F7F2]' : 'border-[#E5E7EB] hover:border-[#8B5E3C]'}`}
          onClick={() => updateConfig({ cabinetType: t.id })}
          data-testid={`cabinet-type-${t.id}`}
        >
          <span className="font-manrope text-lg font-semibold text-[#1A1A1A]">{t.name}</span>
          <span className="text-sm text-[#6B7280] mt-1">{t.description}</span>
          <span className="text-[#8B5E3C] font-medium mt-3">From ${basePrice}</span>
          {t.multiplier > 1 && <span className="text-xs text-[#6B7280]">({t.multiplier}x base price)</span>}
        </div>
      );
    }

    stepContent = (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-2">Select Cabinet Type</h2>
          <p className="text-[#6B7280]">Choose the type of cabinet you want to configure</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{typeCards}</div>
      </div>
    );
  }

  if (step === 2) {
    // Dimensions
    const widthBtns = [];
    for (let i = 0; i < availableWidths.length; i++) {
      const w = availableWidths[i];
      const sel = !config.useCustomSize && config.width === w;
      widthBtns.push(
        <button
          key={w}
          type="button"
          onClick={() => updateConfig({ width: w, useCustomSize: false, customWidth: null })}
          className={`px-4 py-2 border-2 rounded-md font-medium transition-all ${sel ? 'border-[#8B5E3C] bg-[#8B5E3C] text-white' : 'border-[#E5E7EB] hover:border-[#8B5E3C]'}`}
          data-testid={`width-${w}`}
        >
          {w}"
        </button>
      );
    }

    let heightSection = null;
    if (isWallType) {
      const heightBtns = [];
      for (let i = 0; i < availableHeights.length; i++) {
        const h = availableHeights[i];
        const sel = !config.useCustomSize && config.height === h;
        heightBtns.push(
          <button
            key={h}
            type="button"
            onClick={() => updateConfig({ height: h, useCustomSize: false, customHeight: null })}
            className={`px-4 py-2 border-2 rounded-md font-medium transition-all ${sel ? 'border-[#8B5E3C] bg-[#8B5E3C] text-white' : 'border-[#E5E7EB] hover:border-[#8B5E3C]'}`}
            data-testid={`height-${h}`}
          >
            {h}"
          </button>
        );
      }
      heightSection = (
        <div className="space-y-3">
          <Label className="font-medium text-[#1A1A1A]">Height (inches)</Label>
          <div className="flex flex-wrap gap-2">{heightBtns}</div>
        </div>
      );
    } else {
      heightSection = (
        <div className="space-y-3">
          <Label className="font-medium text-[#1A1A1A]">Height</Label>
          <div className="px-4 py-2 bg-[#F9F7F2] rounded-md inline-block">Standard: {BASE_CABINET_HEIGHT}"</div>
        </div>
      );
    }

    stepContent = (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-2">Set Dimensions</h2>
          <p className="text-[#6B7280]">Choose standard sizes or enter custom dimensions</p>
        </div>
        
        <div className="space-y-3">
          <Label className="font-medium text-[#1A1A1A]">Width (inches)</Label>
          <div className="flex flex-wrap gap-2">{widthBtns}</div>
        </div>
        
        {heightSection}

        {/* Custom Size Option */}
        <div className="border-t border-[#E5E7EB] pt-6 mt-6">
          <div className="flex items-center gap-3 mb-4">
            <Checkbox
              checked={config.useCustomSize}
              onCheckedChange={(checked) => updateConfig({ useCustomSize: checked })}
              data-testid="custom-size-toggle"
            />
            <Label className="font-medium text-[#1A1A1A]">Use Custom Dimensions</Label>
            <span className="text-sm text-orange-600">(Base price changes for non-standard sizes)</span>
          </div>
          
          {config.useCustomSize && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-[#F9F7F2] rounded-lg">
              <div className="space-y-2">
                <Label className="text-sm">Custom Width ({MIN_CUSTOM_WIDTH}" - {MAX_CUSTOM_WIDTH}")</Label>
                <Input
                  type="number"
                  step="0.1"
                  min={MIN_CUSTOM_WIDTH}
                  max={MAX_CUSTOM_WIDTH}
                  value={config.customWidth || ''}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    updateConfig({ customWidth: Math.min(MAX_CUSTOM_WIDTH, Math.max(0, val)) });
                  }}
                  placeholder={`Enter width (${MIN_CUSTOM_WIDTH}-${MAX_CUSTOM_WIDTH})`}
                  data-testid="custom-width-input"
                />
              </div>
              {isWallType && (
                <div className="space-y-2">
                  <Label className="text-sm">Custom Height ({MIN_CUSTOM_HEIGHT}" - {MAX_CUSTOM_HEIGHT}")</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min={MIN_CUSTOM_HEIGHT}
                    max={MAX_CUSTOM_HEIGHT}
                    value={config.customHeight || ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      updateConfig({ customHeight: Math.min(MAX_CUSTOM_HEIGHT, Math.max(0, val)) });
                    }}
                    placeholder={`Enter height (${MIN_CUSTOM_HEIGHT}-${MAX_CUSTOM_HEIGHT})`}
                    data-testid="custom-height-input"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Custom Depth Option (only if height > 42") */}
        {showDepthOption && (
          <div className="border-t border-[#E5E7EB] pt-6 mt-6">
            <Label className="font-medium text-[#1A1A1A] block mb-3">
              Custom Depth (for heights over 42")
              <span className="text-sm text-[#6B7280] ml-2">+$7 per inch above {STANDARD_DEPTH}"</span>
            </Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                step="0.1"
                min={MIN_CUSTOM_DEPTH}
                max={MAX_CUSTOM_DEPTH}
                value={config.customDepth || STANDARD_DEPTH}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || STANDARD_DEPTH;
                  updateConfig({ customDepth: Math.max(MIN_CUSTOM_DEPTH, Math.min(MAX_CUSTOM_DEPTH, val)) });
                }}
                className="w-24"
                data-testid="custom-depth-input"
              />
              <span className="text-sm text-[#6B7280]">inches (standard: {STANDARD_DEPTH}", range: {MIN_CUSTOM_DEPTH}-{MAX_CUSTOM_DEPTH})</span>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Label className="font-medium text-[#1A1A1A]">Quantity</Label>
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => updateConfig({ quantity: Math.max(1, config.quantity - 1) })} className="w-10 h-10 border-2 border-[#E5E7EB] rounded-md">-</button>
            <Input type="number" min="1" max="50" value={config.quantity} onChange={(e) => updateConfig({ quantity: Math.max(1, parseInt(e.target.value) || 1) })} className="w-20 text-center" />
            <button type="button" onClick={() => updateConfig({ quantity: Math.min(50, config.quantity + 1) })} className="w-10 h-10 border-2 border-[#E5E7EB] rounded-md">+</button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 3) {
    // Box Material & Door Material
    const boxCards = [];
    for (let i = 0; i < BOX_MATERIALS.length; i++) {
      const m = BOX_MATERIALS[i];
      const sel = config.boxMaterial === m.id;
      const priceInfo = m.multiplier > 1 ? `${m.multiplier}x price` : 'Standard';
      boxCards.push(
        <div
          key={m.id}
          className={`flex flex-col p-4 bg-white border-2 rounded-lg cursor-pointer transition-all ${sel ? 'border-[#8B5E3C] bg-[#F9F7F2]' : 'border-[#E5E7EB] hover:border-[#8B5E3C]'}`}
          onClick={() => updateConfig({ boxMaterial: m.id })}
          data-testid={`box-material-${m.id}`}
        >
          <span className="font-medium text-[#1A1A1A]">{m.name}</span>
          <span className="text-xs text-[#6B7280] mt-1">{m.description}</span>
          <span className="text-xs text-[#8B5E3C] mt-2">{priceInfo}</span>
        </div>
      );
    }

    const doorMatCards = [];
    for (let i = 0; i < DOOR_MATERIALS.length; i++) {
      const m = DOOR_MATERIALS[i];
      const sel = config.doorMaterial === m.id;
      const priceInfo = m.multiplier > 1 ? `${m.multiplier}x price` : 'Standard';
      doorMatCards.push(
        <div
          key={m.id}
          className={`flex flex-col p-4 bg-white border-2 rounded-lg cursor-pointer transition-all ${sel ? 'border-[#8B5E3C] bg-[#F9F7F2]' : 'border-[#E5E7EB] hover:border-[#8B5E3C]'}`}
          onClick={() => updateConfig({ doorMaterial: m.id })}
          data-testid={`door-material-${m.id}`}
        >
          <span className="font-medium text-[#1A1A1A]">{m.name}</span>
          <span className="text-xs text-[#6B7280] mt-1">{m.description}</span>
          <span className="text-xs text-[#8B5E3C] mt-2">{priceInfo}</span>
        </div>
      );
    }

    const styleCards = [];
    for (let i = 0; i < DOOR_STYLES.length; i++) {
      const s = DOOR_STYLES[i];
      const sel = config.doorStyle === s.id;
      styleCards.push(
        <div
          key={s.id}
          className={`flex flex-col p-4 bg-white border-2 rounded-lg cursor-pointer transition-all ${sel ? 'border-[#8B5E3C] bg-[#F9F7F2]' : 'border-[#E5E7EB] hover:border-[#8B5E3C]'}`}
          onClick={() => updateConfig({ doorStyle: s.id })}
          data-testid={`door-style-${s.id}`}
        >
          <span className="font-medium text-[#1A1A1A]">{s.name}</span>
          <span className="text-xs text-[#6B7280] mt-1">{s.description}</span>
        </div>
      );
    }

    stepContent = (
      <div className="space-y-8">
        <div className="text-center mb-8">
          <h2 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-2">Materials & Door Style</h2>
          <p className="text-[#6B7280]">Select cabinet box material, door material, and style</p>
        </div>

        <div className="space-y-4">
          <Label className="font-manrope text-lg font-semibold text-[#1A1A1A]">Cabinet Box Material</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{boxCards}</div>
        </div>

        <div className="space-y-4">
          <Label className="font-manrope text-lg font-semibold text-[#1A1A1A]">Door Material</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{doorMatCards}</div>
        </div>

        <div className="space-y-4">
          <Label className="font-manrope text-lg font-semibold text-[#1A1A1A]">Door Style</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">{styleCards}</div>
        </div>
      </div>
    );
  }

  if (step === 4) {
    // Color Selection
    const colorCards = [];
    for (let i = 0; i < COLORS.length; i++) {
      const c = COLORS[i];
      const sel = config.color === c.id;
      colorCards.push(
        <div
          key={c.id}
          className={`flex flex-col items-center p-4 bg-white border-2 rounded-lg cursor-pointer transition-all ${sel ? 'border-[#8B5E3C] bg-[#F9F7F2]' : 'border-[#E5E7EB] hover:border-[#8B5E3C]'}`}
          onClick={() => updateConfig({ color: c.id })}
          data-testid={`color-${c.id}`}
        >
          <div className="w-12 h-12 rounded-full border-2 border-[#E5E7EB] mb-2 shadow-inner" style={{ backgroundColor: c.hex }} />
          <span className="text-sm font-medium text-[#1A1A1A] text-center">{c.name}</span>
        </div>
      );
    }

    const selColor = findById(COLORS, config.color);

    stepContent = (
      <div className="space-y-8">
        <div className="text-center mb-8">
          <h2 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-2">Door Color</h2>
          <p className="text-[#6B7280]">Choose your cabinet door color</p>
        </div>

        <div className="space-y-4">
          <Label className="font-manrope text-lg font-semibold text-[#1A1A1A]">Available Colors</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{colorCards}</div>
        </div>

        {config.color && (
          <div className="mt-6">
            <Label className="font-manrope text-lg font-semibold text-[#1A1A1A] mb-4 block">Preview</Label>
            <div className="aspect-video relative rounded-lg overflow-hidden border border-[#E5E7EB]">
              <img src={getColorImage(config.color)} alt="Cabinet preview" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="absolute bottom-4 left-4 text-white font-medium">{selColor ? selColor.name : ''} Cabinet</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (step === 5) {
    // Upgrades
    const currentUpgrades = config.upgrades || [];
    const isCornerType = config.cabinetType === 'corner';
    const upgradeCards = [];
    
    for (let i = 0; i < UPGRADES.length; i++) {
      const u = UPGRADES[i];
      const sel = currentUpgrades.includes(u.id);
      const cornerOnly = u.cornerOnly && !isCornerType;
      const cardClass = cornerOnly ? 'opacity-50 cursor-not-allowed border-[#E5E7EB]' : sel ? 'border-[#8B5E3C] bg-[#F9F7F2]' : 'border-[#E5E7EB] hover:border-[#8B5E3C]';
      upgradeCards.push(
        <div
          key={u.id}
          className={`relative p-4 bg-white border-2 rounded-lg cursor-pointer transition-all ${cardClass}`}
          onClick={() => !cornerOnly && toggleUpgrade(u.id)}
          data-testid={`upgrade-${u.id}`}
        >
          <div className="flex items-start gap-3">
            <Checkbox checked={sel} disabled={cornerOnly} className="mt-1" />
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <span className="font-medium text-[#1A1A1A]">{u.name}</span>
                <span className="text-[#8B5E3C] font-medium">+${u.price}</span>
              </div>
              <p className="text-sm text-[#6B7280] mt-1">{u.description}</p>
              {cornerOnly && <p className="text-xs text-orange-500 mt-2">Only for corner cabinets</p>}
            </div>
          </div>
        </div>
      );
    }

    stepContent = (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-2">Premium Upgrades</h2>
          <p className="text-[#6B7280]">Enhance your cabinets with premium features</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{upgradeCards}</div>
      </div>
    );
  }

  if (step === 6) {
    // Review
    const actualWidth = config.useCustomSize && config.customWidth ? config.customWidth : config.width;
    const actualHeight = config.useCustomSize && config.customHeight ? config.customHeight : config.height;
    const actualDepth = config.customDepth || STANDARD_DEPTH;
    const selUpgrades = config.upgrades || [];
    
    const upgradeTags = [];
    for (let i = 0; i < selUpgrades.length; i++) {
      upgradeTags.push(
        <span key={selUpgrades[i]} className="px-3 py-1 bg-[#F9F7F2] text-[#8B5E3C] text-sm rounded-full">
          {getLabel(UPGRADES, selUpgrades[i])}
        </span>
      );
    }

    // Price breakdown
    const breakdown = pricing.breakdown;

    stepContent = (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="font-manrope text-2xl font-bold text-[#1A1A1A] mb-2">Review Your Configuration</h2>
          <p className="text-[#6B7280]">Verify your selections before adding to cart</p>
        </div>
        
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="bg-[#F9F7F2] p-6 border-b border-[#E5E7EB]">
            <h3 className="font-manrope text-xl font-bold text-[#1A1A1A]">Custom {getLabel(CABINET_TYPES, config.cabinetType)}</h3>
            <p className="text-[#6B7280] mt-1">{actualWidth}" W × {actualHeight}" H × {actualDepth}" D</p>
            {breakdown.isCustomSize && <span className="inline-block mt-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">Custom Size (2x pricing)</span>}
          </div>
          
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-[#6B7280]">Cabinet Type:</span> <span className="font-medium">{getLabel(CABINET_TYPES, config.cabinetType)}</span></div>
              <div><span className="text-[#6B7280]">Box Material:</span> <span className="font-medium">{getLabel(BOX_MATERIALS, config.boxMaterial)}</span></div>
              <div><span className="text-[#6B7280]">Door Material:</span> <span className="font-medium">{getLabel(DOOR_MATERIALS, config.doorMaterial)}</span></div>
              <div><span className="text-[#6B7280]">Door Style:</span> <span className="font-medium">{getLabel(DOOR_STYLES, config.doorStyle)}</span></div>
              <div><span className="text-[#6B7280]">Color:</span> <span className="font-medium">{getLabel(COLORS, config.color)}</span></div>
              <div><span className="text-[#6B7280]">Quantity:</span> <span className="font-medium">{config.quantity}</span></div>
            </div>

            {selUpgrades.length > 0 && (
              <div className="pt-4 border-t border-[#E5E7EB]">
                <span className="text-[#6B7280] text-sm">Upgrades:</span>
                <div className="flex flex-wrap gap-2 mt-2">{upgradeTags}</div>
              </div>
            )}

            {/* Price Breakdown */}
            <div className="pt-4 border-t border-[#E5E7EB]">
              <span className="text-[#6B7280] text-sm font-semibold">Price Breakdown:</span>
              <div className="mt-2 text-xs text-[#6B7280] space-y-1">
                <div>Base: ${breakdown.basePrice} × Type ({breakdown.typeMultiplier}x) × Box ({breakdown.boxMultiplier}x) × Door ({breakdown.doorMultiplier}x)</div>
                {breakdown.widthIncrement > 0 && <div>Width increment: +${breakdown.widthIncrement}</div>}
                {breakdown.heightIncrement > 0 && <div>Height increment: +${breakdown.heightIncrement}</div>}
                {breakdown.depthUpgradePrice > 0 && <div>Depth upgrade: +${breakdown.depthUpgradePrice}</div>}
                {breakdown.isCustomSize && (
                  <div className="text-orange-600">
                    Custom size: 2x price of nearest standard ({breakdown.nearestStandardWidth}" W × {breakdown.nearestStandardHeight}" H)
                  </div>
                )}
                {pricing.upgradesTotal > 0 && <div>Upgrades: +${pricing.upgradesTotal}</div>}
              </div>
            </div>
          </div>
          
          <div className="bg-[#1A1A1A] p-6 text-white">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-300">Unit Price:</span>
              <span className="font-medium">${pricing.unitPrice.toLocaleString()}</span>
            </div>
            {config.quantity > 1 && <div className="flex justify-between items-center mb-2 text-sm text-gray-400"><span>× {config.quantity} units</span></div>}
            <div className="flex justify-between items-center pt-4 border-t border-white/20">
              <span className="text-lg font-semibold">Total:</span>
              <span className="text-2xl font-bold text-[#8B5E3C]">${pricing.totalPrice.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step indicators
  const stepIcons = [Package, Ruler, Palette, Palette, Settings, ShoppingCart];
  const stepTitles = ['Type', 'Size', 'Material', 'Color', 'Upgrades', 'Review'];
  const stepIndicators = [];
  for (let i = 0; i < 6; i++) {
    const Icon = stepIcons[i];
    const isActive = (i + 1) === step;
    const isCompleted = (i + 1) < step;
    stepIndicators.push(
      <div key={i} className="flex items-center">
        <div className="flex flex-col items-center">
          <button
            onClick={() => setStep(i + 1)}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${isActive ? 'bg-[#8B5E3C] text-white' : isCompleted ? 'bg-[#1A1A1A] text-white' : 'bg-white border-2 border-[#E5E7EB] text-[#6B7280]'}`}
            data-testid={`step-${i + 1}`}
          >
            {isCompleted ? <Check size={20} /> : <Icon size={20} />}
          </button>
          <span className={`mt-2 text-xs font-medium hidden sm:block ${isActive ? 'text-[#8B5E3C]' : isCompleted ? 'text-[#1A1A1A]' : 'text-[#6B7280]'}`}>{stepTitles[i]}</span>
        </div>
        {i < 5 && <div className={`w-8 sm:w-16 h-0.5 mx-1 sm:mx-2 ${(i + 1) < step ? 'bg-[#1A1A1A]' : 'bg-[#E5E7EB]'}`} />}
      </div>
    );
  }

  return (
    <Layout>
      <section className="relative h-[40vh] min-h-[300px] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `url(${CABINET_IMAGES.hero})` }} data-testid="cabinets-hero">
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 text-center text-white px-6">
          <h1 className="font-manrope text-4xl sm:text-5xl font-extrabold mb-4">Custom Cabinet Builder</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">Design your perfect cabinets with our interactive configurator</p>
        </div>
      </section>

      <section className="py-12 bg-[#F9F7F2]" data-testid="cabinets-configurator">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-12"><div className="flex items-center justify-between max-w-3xl mx-auto">{stepIndicators}</div></div>
          <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 min-h-[400px]">{stepContent}</div>
          <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-lg shadow-sm border border-[#E5E7EB]">
              <Calculator size={20} className="text-[#8B5E3C]" />
              <div>
                <p className="text-sm text-[#6B7280]">Estimated Total</p>
                <p className="font-manrope text-xl font-bold text-[#1A1A1A]" data-testid="price-display">${pricing.totalPrice.toLocaleString()}</p>
              </div>
            </div>
            <div className="flex gap-4">
              {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} className="px-6" data-testid="prev-step-btn"><ChevronLeft size={18} className="mr-1" /> Back</Button>}
              {step < 6 ? <Button onClick={() => setStep(step + 1)} className="bg-[#8B5E3C] hover:bg-[#7a5235] px-8" data-testid="next-step-btn">Next <ChevronRight size={18} className="ml-1" /></Button> : <Button onClick={handleAddToCart} className="bg-[#1A1A1A] hover:bg-[#333333] px-8" data-testid="add-to-cart-btn"><ShoppingCart size={18} className="mr-2" /> Add to Cart</Button>}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-testid="cabinets-info">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6"><div className="w-16 h-16 bg-[#F9F7F2] rounded-full flex items-center justify-center mx-auto mb-4"><Package size={28} className="text-[#8B5E3C]" /></div><h3 className="font-manrope text-lg font-semibold mb-2">Premium Materials</h3><p className="text-[#6B7280] text-sm">High-quality materials with superior finishes.</p></div>
            <div className="text-center p-6"><div className="w-16 h-16 bg-[#F9F7F2] rounded-full flex items-center justify-center mx-auto mb-4"><Ruler size={28} className="text-[#8B5E3C]" /></div><h3 className="font-manrope text-lg font-semibold mb-2">Custom Sizes</h3><p className="text-[#6B7280] text-sm">Standard sizes or fully custom dimensions.</p></div>
            <div className="text-center p-6"><div className="w-16 h-16 bg-[#F9F7F2] rounded-full flex items-center justify-center mx-auto mb-4"><Info size={28} className="text-[#8B5E3C]" /></div><h3 className="font-manrope text-lg font-semibold mb-2">Expert Support</h3><p className="text-[#6B7280] text-sm">Free consultations with our design experts.</p></div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
